import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { HttpErrorHandlerService } from "../helpers/http-error-handler.service";
import { API_ENDPOINT_PATH, API_HTTP_HEADERS } from "../helpers/api-constant";
import { BehaviorSubject, catchError, firstValueFrom, Observable } from "rxjs";
import { IUpdateDTModel } from "../helpers/update-model.interface";
import { IAppGlobalSettings, ICreateAppGlobalSettings, DEFAULT_GLOBAL_SETTINGS} from "./app-global-settings.interface";

@Injectable({
  providedIn: 'root'
})
export class AppGlobalSettingsService {
	readonly #headers: HttpHeaders
	readonly #urlEndpoint: string

	private readonly appGlobalSettingsSubject = new BehaviorSubject<IAppGlobalSettings | null>(null);
	public readonly appGlobalSettings$: Observable<IAppGlobalSettings | null> = this.appGlobalSettingsSubject.asObservable();
	private initialized = false;

	constructor(
		private readonly http: HttpClient,
		private readonly errorHandlerService: HttpErrorHandlerService
	) {
		this.#urlEndpoint = `${API_ENDPOINT_PATH}/appGlobalSettings`;
		this.#headers = new HttpHeaders(API_HTTP_HEADERS);
	}

	private getUrl(): string {
		return `${this.#urlEndpoint}`
	}

	private getHeader(): HttpHeaders {
		return this.#headers
	}

	async initialize(): Promise<IAppGlobalSettings> {
		try {
			const settings = await this.listAll();

			if (settings.length === 0) {
				// Create default settings if none exist
				const created = await this.upsertOne(DEFAULT_GLOBAL_SETTINGS);
				if ('_id' in created) {
					this.appGlobalSettingsSubject.next(created);
					this.initialized = true;
					return created;
				}
			} else {
				// Use existing settings
				this.appGlobalSettingsSubject.next(settings[0]);
				this.initialized = true;
				return settings[0];
			}

			throw new Error('Failed to initialize settings');
		} catch (error) {
			console.error('Error initializing global settings:', error);
			throw error;
		}
	}

	getCurrentSettings(): IAppGlobalSettings | null {
		return this.appGlobalSettingsSubject.value;
	}

	isGravatarEnabled(): boolean {
		const settings = this.appGlobalSettingsSubject.value;
		return settings?.profile.gravatar.enabled ?? false;
	}

	getDefaultAvatarStyle(): string {
		return  'identicon';
	}

	isInitialized(): boolean {
		return this.initialized;
	}

	async upsertOne(payload: IAppGlobalSettings | ICreateAppGlobalSettings): Promise<IAppGlobalSettings | IUpdateDTModel | {}> {
		const result = this.http.post<IAppGlobalSettings | IUpdateDTModel>(`${this.getUrl()}`, payload,
			{ headers: this.getHeader() }).pipe(catchError(this.errorHandlerService.handleErrorForSingleRecord));

		const updated = await firstValueFrom(result);
		if (updated && '_id' in updated) {
			this.appGlobalSettingsSubject.next(updated as IAppGlobalSettings);
		} else if (updated && 'acknowledged' in updated && (updated as IUpdateDTModel).acknowledged) {
			const settings = await this.listAll();
			if (settings.length > 0) {
				this.appGlobalSettingsSubject.next(settings[0]);
				return settings[0];
			}
		}

		return updated;
	}

	async getById(id: string, attr?: string): Promise<IAppGlobalSettings|{}> {
		let queryUrl = `${this.getUrl()}/${id}`
		if (attr) { queryUrl += `?attr=${attr}`}

		const result = this.http.get<IAppGlobalSettings>(`${queryUrl}`,
			{ headers: this.getHeader() }).pipe(catchError(this.errorHandlerService.handleErrorForSingleRecord))
		return await firstValueFrom(result)
	}

	async listAll(params?: string): Promise<IAppGlobalSettings[]> {
		let queryUrl = `${this.getUrl()}`
		if (params) { queryUrl += `?${params}` }

		const result = this.http.get<IAppGlobalSettings[]>(`${queryUrl}`,
			{ headers: this.getHeader() }).pipe(catchError(this.errorHandlerService.handleErrorForRecords))
		return await firstValueFrom(result)
	}

	async reload(): Promise<void> {
		const settings = await this.listAll();
		if (settings.length > 0) {
			this.appGlobalSettingsSubject.next(settings[0]);
		}
	}
}
